/**
 * assets/cart-drawer-recommendations.js
 *
 * Controla a seção "Sugestões" do cart drawer:
 *  - navegação do carrossel (setas)
 *  - seleção de variantes (atualiza preço/imagem/id da variante)
 *  - adicionar ao carrinho via AJAX, sem fechar o drawer
 *
 * Ajuste o método `refreshCart()` para chamar a função de atualização do seu
 * tema (ex.: cart-drawer.js do Prestige) caso o fallback genérico abaixo não
 * bata com a implementação do seu drawer.
 */
(function () {
  'use strict';

  class CartDrawerRecommendations extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cart-recs-track]');
      this.prevBtn = this.querySelector('[data-cart-recs-prev]');
      this.nextBtn = this.querySelector('[data-cart-recs-next]');
      this.items = Array.from(this.querySelectorAll('[data-cart-recs-item]'));

      this.prevBtn && this.prevBtn.addEventListener('click', () => this.scrollByAmount(-1));
      this.nextBtn && this.nextBtn.addEventListener('click', () => this.scrollByAmount(1));
      this.track && this.track.addEventListener('scroll', () => this.updateNavState(), { passive: true });

      this.items.forEach((item) => this.initItem(item));

      this.updateNavState();
    }

    scrollByAmount(direction) {
      if (!this.track) return;
      const amount = this.track.clientWidth * direction;
      this.track.scrollBy({ left: amount, behavior: 'smooth' });
    }

    updateNavState() {
      if (!this.track) return;
      const maxScroll = this.track.scrollWidth - this.track.clientWidth - 1;
      if (this.prevBtn) this.prevBtn.disabled = this.track.scrollLeft <= 0;
      if (this.nextBtn) this.nextBtn.disabled = this.track.scrollLeft >= maxScroll;
    }

    initItem(item) {
      const variantsScript = item.querySelector('[data-cart-recs-variants]');
      const variants = variantsScript ? JSON.parse(variantsScript.textContent) : [];
      const selects = Array.from(item.querySelectorAll('[data-cart-recs-option]'));
      const priceEl = item.querySelector('[data-cart-recs-price]');
      const imageEl = item.querySelector('[data-cart-recs-image]');
      const addBtn = item.querySelector('[data-cart-recs-add]');

      const findMatchingVariant = () => {
        const selectedOptions = selects.map((select) => select.value);
        return variants.find((variant) =>
          selectedOptions.every((value, index) => variant.options[index] === value)
        );
      };

      const applyVariant = (variant) => {
        if (!variant) {
          if (addBtn) addBtn.disabled = true;
          return;
        }
        if (addBtn) {
          addBtn.dataset.variantId = variant.id;
          addBtn.disabled = !variant.available;
        }
        if (priceEl && variant.price !== undefined) {
          priceEl.textContent = this.formatMoney(variant.price);
        }
        if (imageEl && variant.featured_image && variant.featured_image.src) {
          imageEl.src = this.resizeImageUrl(variant.featured_image.src, 300);
        }
      };

      selects.forEach((select) => {
        select.addEventListener('change', () => {
          applyVariant(findMatchingVariant());
        });
      });

      if (addBtn) {
        addBtn.addEventListener('click', () => this.addToCart(addBtn));
      }
    }

    formatMoney(cents) {
      // Fallback simples caso Shopify.formatMoney não esteja disponível no escopo global.
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      return (cents / 100).toLocaleString(document.documentElement.lang || 'pt-BR', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'BRL',
      });
    }

    resizeImageUrl(url, width) {
      return url.replace(/(\.[a-zA-Z0-9]+)(\?.*)?$/, `_${width}x$1$2`);
    }

    async addToCart(button) {
      const variantId = button.dataset.variantId;
      if (!variantId || button.disabled) return;

      button.disabled = true;
      button.classList.add('is-loading');
      const spinner = button.querySelector('[data-cart-recs-spinner]');
      if (spinner) spinner.hidden = false;

      try {
        const response = await fetch(window.Shopify ? `${window.Shopify.routes.root}cart/add.js` : '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) {
          const error = await response.json().catch(() => null);
          throw new Error((error && error.description) || 'Não foi possível adicionar o produto.');
        }

        await this.refreshCart();
      } catch (error) {
        console.error('[cart-drawer-recommendations]', error);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
        if (spinner) spinner.hidden = true;
      }
    }

    /**
     * Atualiza o drawer inteiro (itens, subtotal, contador, e a própria
     * seção de sugestões) reaproveitando a função updateCartDrawer() já
     * definida em cart-drawer.liquid — que busca a section via
     * ?section_id= e substitui o innerHTML de #cart-drawer, preservando a
     * instância do custom element e disparando 'cart:refresh' no final.
     */
    async refreshCart() {
      if (typeof window.updateCartDrawer === 'function') {
        await window.updateCartDrawer();
        return;
      }

      // Fallback apenas para o caso de updateCartDrawer não estar disponível
      // (ex.: cart-drawer.liquid desatualizado sem o `window.updateCartDrawer = ...`).
      console.warn('[cart-drawer-recommendations] window.updateCartDrawer não encontrado, recarregando a página.');
      location.reload();
    }
  }

  if (!customElements.get('cart-drawer-recommendations')) {
    customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
  }
})();